import { Injectable } from '@angular/core';

import { firebase, getStorageForBucket } from '@app/src/firebase';
import { getFileExtension } from 'libs/util';

import { AppConfigService } from '@app/src/app/minimal/services/AppConfig';
import { MingaStoreFacadeService } from '@app/src/app/store/Minga/services';

import { retryWithBackoff } from '@shared/utils';
import { FirebaseStorage } from 'libs/util';

/**
 * Service for uploading student id images to firebase storage and
 * getting the url to student id images in firebase storage.
 */
@Injectable({ providedIn: 'root' })
export class StudentIdImageService {
  constructor(private appConfig: AppConfigService, private mingaService: MingaStoreFacadeService) {}

  private async getMingaHash(): Promise<string> {
    const minga = await this.mingaService.getMingaAsPromise();
    return minga.hash;
  }

  /**
   * Upload the given file to firebase storage as the given student's id image.
   *
   * @param file
   * @param personHash
   * @returns
   */
  async uploadFile(file: File, jobId?: string) {
    const mingaHash = await this.getMingaHash();
    const fileRef = await this._getRefPath(file.name, FirebaseStorage.TempImageDirectory, mingaHash, jobId);
    const task = fileRef.put(file);
    return task;
  }

  async uploadBase64(file: string, name: string, personHash: string) {
    const mingaHash = await this.getMingaHash();
    const extension = getFileExtension(name, true);
    const fileName = personHash + '.' + extension;

    const fileRef = await this._getRefPath(fileName, FirebaseStorage.StudentIdPresetSize.RAW, mingaHash);
    const metadata = {
      customMetadata: {
        personHash,
      },
    };
    const task = fileRef.putString(file, 'data_url', metadata);

    return task;
  }

  /**
   * Get the url to the file for displaying the image. Gets the download
   *  url (secured url) from firebase storage.
   *
   * @param fileName - the filename of the file we want the url to.
   * @returns the download url to the given image.
   */
  async getFilePathToStudentIdImage(
    fileName: string,
    preset: FirebaseStorage.StudentIdPresetSize = FirebaseStorage.StudentIdPresetSize.RAW
  ): Promise<string> {
    const mingaHash = await this.getMingaHash();
    let url = '';
    if (preset !== FirebaseStorage.StudentIdPresetSize.AVATAR) {
      const fileRef = await this._getRefPath(fileName, preset, mingaHash);
      try {
        url = await fileRef.getDownloadURL();
      } catch (e) {
        // if the preset isn't found, use the raw size.
        if (preset !== FirebaseStorage.StudentIdPresetSize.RAW) {
          url = await this.getFilePathToStudentIdImage(fileName, FirebaseStorage.StudentIdPresetSize.RAW);
        } else {
          // after trying the raw size if we still can't find the file, throw the error.
          throw e;
        }
      }
    } else {
      const filePath = FirebaseStorage.getFilePath(fileName, preset, mingaHash);
      const assetPrefix = new URL(await this.appConfig.getAssetUrlPrefix());
      url = assetPrefix + filePath;
      const testLink = await fetch(url, { cache: 'reload' });
      if (!testLink.ok) {
        url = await this.getFilePathToStudentIdImage(fileName, FirebaseStorage.StudentIdPresetSize.RAW);
      }
    }

    return url;
  }

  async getStudentIdImageAsBlob(
    fileName: string,
    preset: FirebaseStorage.StudentIdPresetSize = FirebaseStorage.StudentIdPresetSize.RAW
  ): Promise<Blob> {
    const mingaHash = await this.getMingaHash();
    if (!fileName) {
      return null;
    }
    let url = '';
    const fileRef = await this._getRefPath(fileName, preset, mingaHash);

    try {
      return await retryWithBackoff<Blob>(
        async () => {
          url = await fileRef.getDownloadURL();
          const result = await fetch(url, { cache: 'reload' });

          if (!result.ok) {
            throw new Error('Failed to fetch image');
          }

          return result.blob();
        },
        3,
        200
      );
    } catch (e) {
      // if the preset isn't found, use the raw size.
      if (preset !== FirebaseStorage.StudentIdPresetSize.RAW) {
        return await this.getStudentIdImageAsBlob(fileName, FirebaseStorage.StudentIdPresetSize.RAW);
      }

      // we hit here we've retried a bunch, time to fail
      throw e;
    }
  }

  /**
   * Get the firebase reference for the given file name.
   *
   * @param filename
   * @param preset
   * @returns firebase reference to the given file.
   */
  private async _getRefPath(
    filename: string,
    preset: FirebaseStorage.StudentIdPresetSize = FirebaseStorage.StudentIdPresetSize.RAW,
    mingaHash: string,
    jobId?: string
  ): Promise<firebase.storage.Reference> {
    const storage = await this._getStorage();
    const storageref = storage.ref();
    const filePath = FirebaseStorage.getFilePath(filename, preset, mingaHash, jobId);

    return storageref.child(filePath);
  }

  /**
   * Get a reference to firebase storage
   *
   * @returns Storage
   */
  private async _getStorage(): Promise<firebase.storage.Storage> {
    const idBucket = await this.appConfig.getIdBucket();
    const storage = getStorageForBucket(idBucket);

    return storage;
  }
}
